import { Modal, Switch } from "antd";
import dayjs from "dayjs";
import { get } from "lodash";
import moment from "moment";
import { useState } from "react";
import {
  MdCalendarMonth,
  MdCalendarViewDay,
  MdOutlineChangeCircle,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import { DATE_FORMAT } from "utils/constants";
import { bindDateInputProps } from "utils/helpers/input";

export const step = { CONTRACT: "CONTRACT" };

const Contract = ({
  index,
  translation,
  deltaRia,
  deltaStart,
  deltaEnd,
  deltaEndRia,
  values,
  ...formProps
}) => {
  const [pickerValue, setPickerValue] = useState(
    !get(values, "contract.issuanceDate") &&
      dayjs(moment.max(moment(), moment().startOf("y")).toDate()),
  );
  const [informProspect, setInformProspect] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const checkExpired = ({ issuanceDate, isRia }) => {
    const minIssuanceDate = dayjs()
      .startOf("day")
      .add(isRia ? deltaRia : deltaStart, "days");
    const expired =
      values.expired || dayjs(issuanceDate).isBefore(minIssuanceDate);
    return expired;
  };

  return (
    <>
      {informProspect && (
        <Modal
          open
          width={400}
          onCancel={() => {
            formProps.setFieldValue("ria", false);
            setPickerValue(dayjs().add(1, "y").startOf("y").add(5, "h"));
            setInformProspect(false);
          }}
          footer={null}
        >
          <div className="due-date_modal">
            <b>
              {
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT]
                  .TERMINATION_MODAL.TEXT
              }
            </b>
            <Button
              type="primary"
              alignment="center"
              onClick={() => {
                formProps.setFieldValue("ria", true);
                if (
                  checkExpired({
                    issuanceDate: values?.contract?.issuanceDate,
                    isRia: true,
                  })
                ) {
                  setShowInfo(true);
                  formProps.setFieldValue(
                    "contract.issuanceDate",
                    dayjs().add(deltaRia, "days").toDate(),
                  );
                }
                setPickerValue(dayjs().add(deltaRia, "days"));
                setInformProspect(false);
              }}
            >
              {
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT]
                  .TERMINATION_MODAL.ACTION
              }
            </Button>
          </div>
        </Modal>
      )}
      <div className="contact-stepper" key={step.CONTRACT}>
        <div className="contact-stepper__header">
          <div className="contact-stepper__header--counter">
            <span>{index}</span>
          </div>
          <div className="contact-stepper__header--content">
            <h1 className="contact-stepper__header--content--title">
              <span>
                {translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].TITLE}
              </span>
            </h1>
            <p className="contact-stepper__header--content--description">
              {translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].DESCRIPTION}
            </p>
          </div>
        </div>
        <div className="contact-stepper__content">
          {checkExpired({
            issuanceDate: values?.contract?.issuanceDate,
            isRia: values.ria,
          }) && (
            <Alert
              showIcon
              type="error"
              message={
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].INVALID_DATE
              }
              description={
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT]
                  .INVALID_LMF_ISSUANCE
              }
            />
          )}
          <div className="contact-stepper__content--radio">
            <MdOutlineChangeCircle size={22} />
            <span className="contact-stepper__content--radio--title">
              {
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.CONTRACT
                  .LMF_LABEL
              }
            </span>
            <Switch
              className="contact-stepper__content--radio--switch button__switch"
              checkedChildren={
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.CONTRACT
                  .OPTIONS.YES
              }
              unCheckedChildren={
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.CONTRACT
                  .OPTIONS.NO
              }
              defaultChecked={values.ria}
              checked={values.ria}
              onChange={(value) => {
                if (value) setInformProspect(true);
                else {
                  formProps.setFieldValue("ria", false);
                  formProps.setFieldValue("terminationRequest.dueDate", null);
                }
              }}
            />
          </div>
          {get(values, "ria") && (
            <Input
              icon={<MdCalendarMonth size={22} />}
              label={
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.DUE_DATE
                  .LABEL
              }
              placeholder={
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.DUE_DATE
                  .PLACEHOLDER
              }
              type="date"
              variant="simple"
              format={DATE_FORMAT}
              disabledDate={(current) => current && current > dayjs()}
              {...bindDateInputProps({
                name: "terminationRequest.dueDate",
                values,
                ...formProps,
              })}
              onBlur={({ target: { value } }) => {
                const date = dayjs(value, DATE_FORMAT).add(2, "h");
                const isValid = date.isValid() && date < dayjs();
                if (!isValid) return;
                formProps.setFieldValue("terminationRequest.dueDate", date);
              }}
            />
          )}
          <Input
            icon={<MdCalendarViewDay size={22} />}
            label={
              translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.START_DATE
                .LABEL
            }
            placeholder={
              translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.START_DATE
                .PLACEHOLDER
            }
            type="date"
            variant="simple"
            format={DATE_FORMAT}
            disabledDate={(current) => {
              const validStartDate = values.ria
                ? dayjs(
                    moment
                      .max(
                        moment().add(deltaRia, "days").startOf("day"),
                        moment().startOf("y"),
                      )
                      .toDate(),
                  )
                : dayjs(
                    moment
                      .max(
                        moment().add(deltaStart, "days").startOf("day"),
                        moment().startOf("y"),
                      )
                      .toDate(),
                  );
              return current && current < validStartDate;
            }}
            defaultPickerValue={pickerValue}
            {...bindDateInputProps({
              name: "contract.issuanceDate",
              values,
              ...formProps,
            })}
          />
          <>
            {showInfo && (
              <Alert
                showIcon
                type="info"
                message={
                  translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT]
                    .INVALID_LMF_ISSUANCE
                }
              />
            )}
          </>
          {/* <div className="contact-stepper__content--radio">
          <MdSocialDistance size={22} />
          <span className="contact-stepper__content--radio--title">
            {
              translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM
                .CONTRACT_TYPE.LABEL
            }
          </span>
          <Switch
            className="contact-stepper__content--radio--switch button__switch"
            checkedChildren={
              translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM
                .CONTRACT_TYPE.OPTIONS.YES
            }
            unCheckedChildren={
              translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM
                .CONTRACT_TYPE.OPTIONS.NO
            }
            onChange={(value, _) => {
              formProps.setFieldValue("fields.twoTimesSale", value);
            }}
          />
        </div> */}
        </div>
      </div>
    </>
  );
};
export default Contract;
